import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import { ReactComponent as PlayIcon } from 'studio_react/svg/common/play.svg';
import { MUSIC_EVENTS } from 'Sp/Angular/Events/Client/Gallery';
import { ISpAuthorizationToken, useClientSlideshow } from 'ts/client/common';
import LoadingIndicator from 'ts/common/components/LoadingIndicator';
import {
    ISlideshowPlayerTickerHandle,
    SlideshowPlayerTicker
} from 'ts/common/components/slideshows';
import { getSlideshowPlaybackDurationMs } from 'ts/common/components/slideshows/slideshowAudioTimeline';
import {
    buildOverlapTimeline,
    getBoundaryTransitionDurations,
    getSlideDurationMs
} from 'ts/common/components/slideshows/slideshowOverlapTimeline';
import SlideshowControls from './SlideshowPlayer/SlideshowControls';

interface Props {
    photoId: number;
    albumId?: number | string | null;
    spAuthorizationToken: ISpAuthorizationToken;
    spEventBus: SpAngularJs.ISpEventBus;
}

const ClientSlideshowViewer: FC<Props> = ({
    photoId,
    albumId,
    spAuthorizationToken,
    spEventBus
}) => {
    const { response, loading, watermarkUrl, isWatermarkReady, handleAudioActiveChange } =
        useClientSlideshow({ photoId, albumId, spAuthorizationToken, spEventBus });

    const [isPlaying, setIsPlaying] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [currentTimeMs, setCurrentTimeMs] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const tickerRef = useRef<ISlideshowPlayerTickerHandle>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Reset playback state whenever the slideshow changes
    useEffect(() => {
        if (response?.type === 'event-slideshow' && response.slides.length > 0) {
            setCurrentTimeMs(0);
            setIsPlaying(false);
            setHasStarted(false);
        }
    }, [response]);

    // Track fullscreen state
    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(document.fullscreenElement === containerRef.current);
        };

        document.addEventListener('fullscreenchange', onFullscreenChange);

        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    const totalDurationMs = useMemo(() => {
        if (response?.type !== 'event-slideshow' || !response.slides.length) {
            return 0;
        }

        const layout = buildOverlapTimeline(
            response.slides.map(getSlideDurationMs),
            getBoundaryTransitionDurations(response),
            { loop: response.loopToFitMusic }
        );

        return getSlideshowPlaybackDurationMs(layout.totalDuration, response.songSegments);
    }, [response]);

    // Tracks whether the slideshow is currently claiming exclusive audio, so a competing
    // gallery-music start (below) knows whether to react.
    const isAudioActiveRef = useRef(false);

    const handleAudioActiveChangeAndTrack = useCallback(
        (isActive: boolean) => {
            isAudioActiveRef.current = isActive;
            handleAudioActiveChange(isActive);
        },
        [handleAudioActiveChange]
    );

    // Mirror image of the duck above: if the visitor (re)starts gallery background music while
    // this slideshow's own music is playing, pause the whole slideshow instead of playing both
    // at once — silencing just the audio would leave the slides advancing with nothing
    // narrating them. Deliberately does not auto-resume when gallery music later pauses — only
    // *starting* one side should affect the other, never resuming it.
    useEffect(() => {
        return spEventBus.on(MUSIC_EVENTS.GALLERY_PLAYING, () => {
            if (isAudioActiveRef.current) {
                setIsPlaying(false);
            }
        });
    }, [spEventBus]);

    // The lightbox filmstrip keeps a window of nearby photos mounted for animation, so navigating
    // to an adjacent photo does not by itself unmount this viewer — stop playback explicitly on
    // every navigation so a slideshow left mounted off-screen doesn't keep playing (and sounding)
    // silently in the background.
    useEffect(() => {
        return spEventBus.on(MUSIC_EVENTS.STOP_SLIDESHOW, () => {
            setIsPlaying(false);
        });
    }, [spEventBus]);

    const handlePlayPause = useCallback(() => {
        setIsPlaying((p) => !p);
        setHasStarted(true);
    }, []);

    const handlePlaybackEnd = useCallback(() => {
        setIsPlaying(false);
    }, []);

    const handleSeek = useCallback((timeSeconds: number) => {
        const timeMs = timeSeconds * 1000;
        tickerRef.current?.seek(timeMs);
        setCurrentTimeMs(timeMs);
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        if (document.fullscreenElement === containerRef.current) {
            document.exitFullscreen();
        } else if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen();
        }
    }, []);

    if (loading) {
        return (
            <Box
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <LoadingIndicator />
            </Box>
        );
    }

    if (response?.type !== 'event-slideshow' || response.slides.length === 0) {
        return null;
    }

    const aspectRatio = response.aspectRatio ?? '16:9';
    const [arW, arH] = aspectRatio.split(':').map(Number);

    return (
        // Outer: fills the lightbox, centers the aspect-ratio-constrained inner box.
        // containerType: size establishes the container query context for the inner box.
        // No background here — the lightbox's own background shows in the areas not
        // covered by the player canvas, making different aspect ratios visually distinct.
        <Box
            ref={containerRef}
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="default"
            sx={{ containerType: 'size' }}
        >
            {/*
             * Pointer cursor + onClick: clicking the canvas toggles play/pause.
             * The controls' inner Flex calls e.stopPropagation(), so control-button
             * clicks never bubble up to this handler.
             */}
            <Box
                position="relative"
                overflow="hidden"
                cursor="pointer"
                onClick={hasStarted ? handlePlayPause : undefined}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                maxW="100%"
                maxH="100%"
                aspectRatio={`${arW} / ${arH}`}
                sx={{
                    width: `min(100cqw, calc(100cqh * ${arW} / ${arH}))`,
                    height: `min(100cqh, calc(100cqw * ${arH} / ${arW}))`
                }}
            >
                {isWatermarkReady ? (
                    <SlideshowPlayerTicker
                        ref={tickerRef}
                        slideshow={response}
                        isPlaying={isPlaying}
                        onTimeChange={setCurrentTimeMs}
                        onPlaybackEnd={handlePlaybackEnd}
                        watermarkUrl={watermarkUrl}
                        onAudioActiveChange={handleAudioActiveChangeAndTrack}
                        loadingIndicator={<LoadingIndicator />}
                    />
                ) : (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LoadingIndicator />
                    </Box>
                )}

                {/* Big play circle — covers the canvas before first play.
                    Has its own onClick so there is no competing handler on the canvas Box. */}
                {!hasStarted && (
                    <Box
                        position="absolute"
                        top={0}
                        right={0}
                        bottom={0}
                        left={0}
                        zIndex={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        onClick={handlePlayPause}
                    >
                        <Box
                            w="100px"
                            h="100px"
                            borderRadius="full"
                            bg="white"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            boxShadow="0px 10px 32px 0px rgba(0,0,0,0.14)"
                            pl="12px"
                            color="gray.500"
                        >
                            <PlayIcon width={64} height={64} />
                        </Box>
                    </Box>
                )}

                {/* Controls — visible on hover or when paused, only after first play */}
                {hasStarted && (isHovered || !isPlaying) && (
                    <SlideshowControls
                        isPlaying={isPlaying}
                        currentTime={currentTimeMs / 1000}
                        totalDuration={totalDurationMs / 1000}
                        isFullscreen={isFullscreen}
                        onPlayPause={handlePlayPause}
                        onSeek={handleSeek}
                        onToggleFullscreen={handleToggleFullscreen}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ClientSlideshowViewer;
